import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import Button from '../../components/button/Button'
import TenancySwitcher from '../../components/tenancy-switcher/TenancySwitcher'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import {
  getChargeBreakdown,
  getMyRentShare,
  getPaymentOptions,
  setAutopay,
} from '../../_data/resident'
import {
  ChargeSettlement,
  RenterPaymentOptions,
  ResidentPayerLedger,
  ResidentPaymentMethodKind,
} from '../../_types/resident'
import {
  formatCents,
  formatChargeMeta,
  formatLedgerAcknowledgement,
  formatLedgerDate,
  formatLedgerSource,
  formatLedgerStatus,
  getLedgerStatusTone,
} from '../../_utilities/resident-format'
import {
  RENT_HANDLED_ELSEWHERE_MESSAGE,
  isRentHandledElsewhere,
  resolveManagedBalanceSummary,
} from '../../_utilities/ledger-keeper'
import {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  SectionTitle,
  BalanceLabel,
  BalanceAmount,
  LedgerList,
  ChargeList,
  ChargeRow,
  ChargeRowLeft,
  ChargeRowRight,
  LedgerRow,
  LedgerRowLeft,
  LedgerDescription,
  LedgerAmount,
  LedgerMeta,
  QuietText,
  BalanceActions,
  PayHint,
  PaymentBanner,
  AutopaySection,
  AutopayRow,
  AutopayText,
  AutopayLabel,
  AutopayHint,
  PaidSummary,
  LedgerRowActions,
  StatusPill,
  ReceiptLink,
} from './styled'

/**
 * The renter's Payments surface for the selected tenancy: outstanding balance,
 * pay-by-bank / pay-by-card, the autopay toggle, and the charges-and-payments
 * ledger. The amount is always decided server-side from the ledger balance —
 * never sent from here. Tenancy + ledger come from the shared resident context.
 *
 * @returns {JSX.Element}
 */
const PaymentsPage = (): JSX.Element => {
  const {
    selectedTenancy,
    selectedTenancyId,
    isLoadingTenancies,
    ledger,
    isLoadingLedger,
    refreshTenancies,
  } = useResidentTenancy()

  const navigate = useNavigate()
  const [isAutopayLoading, setIsAutopayLoading] = useState<boolean>(false)
  const [autopayError, setAutopayError] = useState<string>('')
  const [paymentOptions, setPaymentOptions] =
    useState<RenterPaymentOptions | null>(null)
  const [myShare, setMyShare] = useState<ResidentPayerLedger | null>(null)
  const [payableCharges, setPayableCharges] = useState<ChargeSettlement[]>([])
  const [searchParams] = useSearchParams()

  // Loaded alongside the ledger so a resident can pay one thing rather than
  // everything. A failure leaves the list empty, which quietly falls back to the
  // whole-balance buttons rather than blocking payment on a display feature.
  useEffect(() => {
    if (selectedTenancyId === '') {
      setPayableCharges([])
      return undefined
    }

    let isMounted = true
    void getChargeBreakdown(selectedTenancyId).then(report => {
      if (isMounted === true) {
        setPayableCharges(
          (report?.charges ?? []).filter(charge => charge.outstandingCents > 0),
        )
      }
    })
    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  useEffect(() => {
    if (selectedTenancyId === '') {
      setMyShare(null)
      return undefined
    }

    let isMounted = true
    void getMyRentShare(selectedTenancyId).then(share => {
      if (isMounted === true) {
        setMyShare(share)
      }
    })
    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  useEffect(() => {
    if (selectedTenancyId === '') {
      setPaymentOptions(null)
      return undefined
    }

    let isMounted = true
    void getPaymentOptions(selectedTenancyId).then(options => {
      if (isMounted === false) {
        return
      }
      if (options === null) {
        setPaymentOptions({
          isCardEnabled: true,
          doesRenterPayCardFee: true,
          autopayPosture: 'available',
        })
        return
      }
      setPaymentOptions(options)
    })
    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  const handlePayRent = useCallback(
    (paymentMethodKind: ResidentPaymentMethodKind): void => {
      if (selectedTenancyId === '') {
        return
      }

      navigate(`/pay/${selectedTenancyId}?method=${paymentMethodKind}`)
    },
    [selectedTenancyId, navigate],
  )

  // Paying one charge reuses the same pay page, which prices it from the server
  // and renders the same fee disclosure. The method is not chosen here: the pay
  // page defaults to bank, the cheap rail, and the resident can still switch.
  const handlePayCharge = useCallback(
    (chargeId: string): void => {
      if (selectedTenancyId === '') {
        return
      }

      navigate(`/pay/${selectedTenancyId}?method=bank&charge=${chargeId}`)
    },
    [selectedTenancyId, navigate],
  )

  // Autopay is per person on a shared home: each roommate has their own mandate
  // and their own on/off state. Reading the tenancy's here would show a co-payer
  // the primary renter's setting and let them toggle it.
  const isAutopayOn =
    myShare !== null
      ? myShare.isAutopayEnabled === true
      : selectedTenancy?.isAutopayEnabled === true

  const handleAutopayToggle = useCallback(async (): Promise<void> => {
    if (selectedTenancy === null) {
      return
    }

    setAutopayError('')
    setIsAutopayLoading(true)

    if (isAutopayOn === true) {
      const wasDisabled = await setAutopay(selectedTenancy.id, false)
      if (wasDisabled === false) {
        setIsAutopayLoading(false)
        setAutopayError('We could not turn off autopay. Please try again.')
        return
      }

      await refreshTenancies()
      setIsAutopayLoading(false)
      return
    }

    setIsAutopayLoading(false)
    navigate(`/autopay/${selectedTenancy.id}`)
  }, [selectedTenancy, isAutopayOn, refreshTenancies, navigate])

  const paymentStatus = searchParams.get('payment')
  const autopayStatus = searchParams.get('autopay')
  let banner: { tone: 'success' | 'cancelled'; message: string } | null = null
  if (autopayError !== '') {
    banner = { tone: 'cancelled', message: autopayError }
  } else if (paymentStatus === 'success') {
    banner = {
      tone: 'success',
      message:
        'Thanks! Your payment is processing. Your balance will update once it clears.',
    }
  } else if (paymentStatus === 'cancel') {
    banner = {
      tone: 'cancelled',
      message: 'Payment cancelled. You can try again whenever you like.',
    }
  } else if (autopayStatus === 'success') {
    banner = {
      tone: 'success',
      message:
        'Autopay setup received. It switches on in a moment, then charges your rent automatically each month from your bank account.',
    }
  } else if (autopayStatus === 'cancel') {
    banner = {
      tone: 'cancelled',
      message: 'Autopay setup cancelled. You can set it up any time.',
    }
  }

  // On a shared home this renter pays THEIR share, not the home's balance, so the
  // figure on the button and the figure in the hero both come from the share when
  // one exists. The server prices the payment from the same place regardless of
  // what was rendered, so a stale share here can only mis-display, never overcharge.
  const owedCents =
    myShare !== null ? myShare.balanceCents : (ledger?.balanceCents ?? 0)

  let balanceText = '-'
  if (isLoadingLedger === true) {
    balanceText = 'Loading...'
  } else if (myShare !== null || ledger !== null) {
    balanceText = formatCents(owedCents)
  }

  // When the property manager keeps the books elsewhere, the ledger here is not
  // what this renter owes. Its balance, its lines and every way of paying against
  // it are withheld, rather than showing an empty ledger as nothing owing.
  const isRentKeptElsewhere = isRentHandledElsewhere(selectedTenancy)
  const managedBalance = resolveManagedBalanceSummary(
    selectedTenancy,
    Date.now(),
  )

  const canPayRent =
    owedCents > 0 && isLoadingLedger === false && isRentKeptElsewhere === false

  const isCardAvailable =
    paymentOptions === null || paymentOptions.isCardEnabled === true
  let payHintText =
    'Bank has no added fee. Card adds a processing fee, shown on the payment page before you confirm.'
  if (isCardAvailable === false) {
    payHintText = 'Payments are made by bank transfer, with no added fee.'
  } else if (
    paymentOptions !== null &&
    paymentOptions.doesRenterPayCardFee === false
  ) {
    payHintText = 'No added fee for bank or card. Pay whichever way you like.'
  }

  const autopayPosture = paymentOptions?.autopayPosture ?? 'available'
  let autopayHintText =
    'Turn on autopay to have rent charged automatically each month, with no card fee.'
  if (selectedTenancy !== null && isAutopayOn === true) {
    autopayHintText =
      'Your rent is charged automatically each month from your saved bank account.'
  } else if (autopayPosture === 'required') {
    autopayHintText =
      'Your property manager asks residents to pay by autopay here. Set it up once and rent takes care of itself, with no card fee.'
  } else if (autopayPosture === 'encouraged') {
    autopayHintText =
      'Your property manager recommends autopay. Rent is charged automatically each month, with no card fee.'
  }

  // Itemised paying is hidden on a home whose payments are split. There a
  // charge belongs to the unit while a payment belongs to one roommate, so
  // "pay this one" has no single correct meaning yet and the server refuses it.
  // Showing a button that is guaranteed to fail is worse than not showing it.
  // Hidden for a single charge too: with one line, the itemised list and the
  // balance above it are the same number said twice.
  const isItemisedPayAvailable =
    selectedTenancy !== null &&
    selectedTenancy.hasRentShares !== true &&
    myShare === null &&
    payableCharges.length > 1 &&
    isRentKeptElsewhere === false

  const currentYear = new Date().getFullYear()
  let paidThisYearCents = 0
  if (ledger !== null) {
    paidThisYearCents = ledger.entries
      .filter(
        entry =>
          entry.type === 'payment' &&
          entry.status === 'settled' &&
          new Date(entry.createdAt).getFullYear() === currentYear,
      )
      .reduce((total, entry) => total + entry.amountCents, 0)
  }

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <PageHeader>
          <PageTitle>Payments</PageTitle>
          <PageSubtitle>
            Pay your rent, manage autopay, and review your ledger.
          </PageSubtitle>
        </PageHeader>

        {banner !== null ? (
          <PaymentBanner tone={banner.tone}>{banner.message}</PaymentBanner>
        ) : null}

        <TenancySwitcher />

        {isLoadingTenancies === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading your payments...</QuietText>
          </CardSurface>
        ) : null}

        {isLoadingTenancies === false && selectedTenancy === null ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have any tenancies yet. When your property manager
              invites you, claim the invite from your email to see your payments
              here.
            </QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null ? (
          <div>
            <SectionTitle>{selectedTenancy.unitLabel} ledger</SectionTitle>
            <CardSurface padding="standard">
              {isRentKeptElsewhere ? (
                <>
                  <BalanceLabel>
                    {managedBalance === null ? 'Rent' : managedBalance.label}
                  </BalanceLabel>
                  {managedBalance === null ? null : (
                    <div>
                      <BalanceAmount>{managedBalance.amountText}</BalanceAmount>
                    </div>
                  )}
                  <QuietText>
                    {managedBalance === null
                      ? RENT_HANDLED_ELSEWHERE_MESSAGE
                      : managedBalance.noteText}
                  </QuietText>
                </>
              ) : (
                <>
                  <BalanceLabel>
                    {myShare === null ? 'Balance owing' : 'Your share owing'}
                  </BalanceLabel>
                  <div>
                    <BalanceAmount>{balanceText}</BalanceAmount>
                  </div>
                </>
              )}

              {myShare === null || isRentKeptElsewhere ? null : (
                <PaidSummary>
                  {`You pay ${
                    myShare.basis === 'fixed'
                      ? formatCents(myShare.fixedCents)
                      : `${myShare.percentBasisPoints / 100}%`
                  } of this home. The amount above is yours alone, not the whole home's.`}
                </PaidSummary>
              )}

              {paidThisYearCents > 0 && isRentKeptElsewhere === false ? (
                <PaidSummary>
                  Paid in {currentYear}: {formatCents(paidThisYearCents)}
                </PaidSummary>
              ) : null}

              {canPayRent ? (
                <>
                  <BalanceActions>
                    <Button
                      type="button"
                      variant="primary"
                      size="small"
                      onClick={() => {
                        handlePayRent('bank')
                      }}
                    >
                      Pay by bank
                    </Button>
                    {isCardAvailable === true ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="small"
                        onClick={() => {
                          handlePayRent('card')
                        }}
                      >
                        Pay by card
                      </Button>
                    ) : null}
                  </BalanceActions>
                  <PayHint>{payHintText}</PayHint>
                </>
              ) : null}

              {isItemisedPayAvailable ? (
                <>
                  <SectionTitle>What this is made of</SectionTitle>
                  <ChargeList>
                    {payableCharges.map(charge => (
                      <ChargeRow key={charge.chargeId}>
                        <ChargeRowLeft>
                          <LedgerDescription>
                            {charge.description}
                          </LedgerDescription>
                          <LedgerMeta>{formatChargeMeta(charge)}</LedgerMeta>
                        </ChargeRowLeft>
                        <ChargeRowRight>
                          <LedgerAmount>
                            {formatCents(charge.outstandingCents)}
                          </LedgerAmount>
                          {charge.isPayable === true ? (
                            <Button
                              type="button"
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                handlePayCharge(charge.chargeId)
                              }}
                            >
                              Pay this
                            </Button>
                          ) : null}
                        </ChargeRowRight>
                      </ChargeRow>
                    ))}
                  </ChargeList>
                </>
              ) : null}

              {ledger !== null &&
              ledger.entries.length === 0 &&
              isRentKeptElsewhere === false ? (
                <QuietText>No charges or payments yet.</QuietText>
              ) : null}

              {ledger !== null &&
              ledger.entries.length > 0 &&
              isRentKeptElsewhere === false ? (
                <LedgerList>
                  {ledger.entries.map(entry => (
                    <LedgerRow key={entry.id}>
                      <LedgerRowLeft>
                        <LedgerDescription>
                          {entry.description}
                        </LedgerDescription>
                        <LedgerMeta>
                          {formatLedgerDate(entry.createdAt)} -{' '}
                          {formatLedgerSource(entry)}
                        </LedgerMeta>
                        {formatLedgerAcknowledgement(entry) !== '' ? (
                          <LedgerMeta>
                            {formatLedgerAcknowledgement(entry)}
                          </LedgerMeta>
                        ) : null}
                        <LedgerRowActions>
                          <StatusPill tone={getLedgerStatusTone(entry.status)}>
                            {formatLedgerStatus(entry.status)}
                          </StatusPill>
                          {entry.type === 'payment' &&
                          entry.status === 'settled' ? (
                            <ReceiptLink
                              type="button"
                              onClick={() => navigate(`/receipt/${entry.id}`)}
                            >
                              View receipt
                            </ReceiptLink>
                          ) : null}
                        </LedgerRowActions>
                      </LedgerRowLeft>
                      <LedgerAmount>
                        {formatCents(entry.amountCents)}
                      </LedgerAmount>
                    </LedgerRow>
                  ))}
                </LedgerList>
              ) : null}

              {/* Kept only while autopay is still on, so a renter can always
                  switch it off, whoever keeps the books. */}
              {isRentKeptElsewhere && isAutopayOn === false ? null : (
                <AutopaySection>
                  <AutopayRow>
                    <AutopayText>
                      <AutopayLabel>
                        {isAutopayOn ? 'Autopay is on' : 'Autopay is off'}
                      </AutopayLabel>
                      <AutopayHint>{autopayHintText}</AutopayHint>
                    </AutopayText>
                    <Button
                      type="button"
                      variant={isAutopayOn ? 'secondary' : 'primary'}
                      size="small"
                      isLoading={isAutopayLoading}
                      disabled={isAutopayLoading}
                      onClick={() => {
                        void handleAutopayToggle()
                      }}
                    >
                      {isAutopayOn ? 'Turn off' : 'Set up autopay'}
                    </Button>
                  </AutopayRow>
                </AutopaySection>
              )}
            </CardSurface>
          </div>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default PaymentsPage
